import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const HeroContainer = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => `${theme.colors.primary}dd`} 100%);
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.md}`};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.sm}`};
  }
`;

const Content = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.desktop};
  width: 100%;
  text-align: center;
  z-index: 10;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xxl};
  margin-top: 150px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 8vw, 4rem);
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.background};
  line-height: 1.1;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const Tagline = styled(motion.p)`
  font-size: clamp(1rem, 4vw, 1.6rem);
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.background};
  opacity: 0.95;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    max-width: 600px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    max-width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  justify-content: center;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 0;
  }
`;

const DownloadButton = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 16px;
  background-color: #000000;
  color: #ffffff;
  border-radius: 8px;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  min-width: 200px;
  border: 1px solid #a6a6a6;
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 180px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 280px;
    min-width: unset;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    background-color: #1a1a1a;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      transform: translateY(-1px);
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const IconContainer = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AppleIcon = styled.div`
  width: 24px;
  height: 28px;
  background-color: white;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'%3E%3Cpath d='M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z'/%3E%3C/svg%3E") no-repeat;
  mask-size: contain;
`;

const GooglePlayIcon = styled.div`
  width: 24px;
  height: 28px;
  background: linear-gradient(45deg, #ea4335 0%, #fbbc04 25%, #34a853 50%, #4285f4 75%);
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6 25 19 25 35v442c0 16 9 29 22 35l247.4-247.4L47 0zM472.9 188.7L351.4 126l-65.4 65.4 65.4 65.4 121.5-62.7c15-8 15-32 0-40zM104.6 499l220.7-221.3 60.1 60.1L104.6 499z'/%3E%3C/svg%3E") no-repeat;
  mask-size: contain;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const DownloadText = styled.div`
  font-size: 11px;
  line-height: 1;
  margin-bottom: 2px;
  opacity: 0.8;
`;

const StoreText = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
`;

const pulseKeyframes = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.18; }
  50% { transform: scale(1.12); opacity: 0.08; }
`;

const Circle = styled.div<{ size: number; top: number; left: number; delay?: number }>`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  top: ${({ top }) => top}%;
  left: ${({ left }) => left}%;
  pointer-events: none;
  will-change: transform, opacity;
  animation: ${pulseKeyframes} ${({ delay }) => 12 + (delay || 0)}s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || 0}s;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: ${({ size }) => size * 0.7}px;
    height: ${({ size }) => size * 0.7}px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ size }) => size * 0.5}px;
    height: ${({ size }) => size * 0.5}px;
    opacity: 0.5;
  }
`;

const FeatureText = styled(motion.div)`
  margin-top: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.background};
  opacity: 0.8;
  font-size: clamp(0.8rem, 2vw, 1rem);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 0 ${({ theme }) => theme.spacing.sm};
  }
`;

interface HeroProps {
  isAppLoading?: boolean;
}

const Hero: React.FC<HeroProps> = ({ isAppLoading = false }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    // Only start animation when app loading is complete
    if (!isAppLoading) {
      // Reset animation key and trigger animation
      setAnimationKey(prev => prev + 1);
      setIsLoaded(false);

      // Small delay to ensure the loading screen has fully disappeared
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 300); // Extra delay to ensure loading screen transition is complete

      return () => clearTimeout(timer);
    } else {
      // Reset state when app is loading
      setIsLoaded(false);
    }
  }, [isAppLoading]); // Depend on isAppLoading to trigger when loading finishes

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <HeroContainer aria-label="Radyofone - Stream Music Anytime Anywhere">
      {isLoaded && (
        <>
          <Circle size={200} top={20} left={10} delay={0} />
          <Circle size={300} top={60} left={80} delay={3} />
        </>
      )}

      <Content>
        <motion.div
          key={animationKey}
          variants={containerVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
        >
          <Title variants={itemVariants}>
            Stream <span style={{ color: '#ff8f31' }}>Music</span> Anytime, Anywhere
          </Title>

          <Tagline variants={itemVariants}>
            Discover <span style={{ color: '#ff8f31', fontWeight: 'bolder' }}>Radyofone</span> – Your Ultimate Radio Streaming Experience
          </Tagline>

          <ButtonContainer>
            <DownloadButton
              href="https://apps.apple.com/lb/app/radyofone/id6752834381"
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).gtag) {
                  (window as any).gtag('event', 'download_click', {
                    event_category: 'App Download',
                    event_label: 'App Store (iOS)',
                    value: 1
                  });
                }
              }}
            >
              <ButtonContent>
                <IconContainer>
                  <AppleIcon />
                </IconContainer>
                <TextContainer>
                  <DownloadText>Download on the</DownloadText>
                  <StoreText>App Store</StoreText>
                </TextContainer>
              </ButtonContent>
            </DownloadButton>

            <DownloadButton
              href="https://play.google.com/store/apps/details?id=com.radyofone.RadyoFone&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).gtag) {
                  (window as any).gtag('event', 'download_click', {
                    event_category: 'App Download',
                    event_label: 'Google Play (Android)',
                    value: 1
                  });
                }
              }}
            >
              <ButtonContent>
                <IconContainer>
                  <GooglePlayIcon />
                </IconContainer>
                <TextContainer>
                  <DownloadText>GET IT ON</DownloadText>
                  <StoreText>Google Play</StoreText>
                </TextContainer>
              </ButtonContent>
            </DownloadButton>
          </ButtonContainer>

          <FeatureText variants={itemVariants}>
            Available on iOS and Android • Free Download • Premium Features Available
          </FeatureText>
        </motion.div>
      </Content>
    </HeroContainer>
  );
};

export default Hero;